import { useState, useEffect } from 'react';
import { fetchWorkshop, fetchLocation } from '../services/workshopService';

/**
 * Custom hook for fetching workshop data
 * Loads workshop + location data and handles all states
 *
 * @param {string} workshopId - Workshop ID from URL
 * @returns {Object} { workshop, location, loading, error }
 */
export const useWorkshopData = (workshopId) => {
  const [workshop, setWorkshop] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workshopId) {
      setError('Workshop ID is required');
      setLoading(false);
      return;
    }

    const loadWorkshopData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch workshop data
        const workshopData = await fetchWorkshop(workshopId);

        // Validate workshop has required fields
        if (!workshopData.title || !workshopData.schedule) {
          throw new Error('Workshop missing required fields');
        }

        // Check if workshop date is in future
        const workshopDate = new Date(workshopData.schedule.date);
        if (workshopDate < new Date()) {
          console.warn('Workshop date is in the past');
        }

        setWorkshop(workshopData);

        // Fetch location data if locationId exists
        if (workshopData.locationId) {
          const locationData = await fetchLocation(workshopData.locationId);
          setLocation(locationData);
        }
      } catch (err) {
        setError(err.message || 'Failed to load workshop data');
        console.error('Error loading workshop:', err);
      } finally {
        setLoading(false);
      }
    };

    loadWorkshopData();
  }, [workshopId]);

  return {
    workshop,
    location,
    loading,
    error,
  };
};
